import React, { useState } from 'react';
import { Sparkles, Share2, Headphones, Settings } from 'lucide-react';
import Footer from './Footer';
import LinkBanner from './LinkBanner';
import Modal from './Modal';
import DownloadButtons from './DownloadButtons';

const BiteSizRLogo = () => (
  <svg width="40" height="40" viewBox="0 0 115 115" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
    <path fillRule="evenodd" clipRule="evenodd" d="M30 0C13.4315 0 0 13.4315 0 30V85C0 101.569 13.4315 115 30 115H85.4632C100.232 115 112.508 104.328 115.001 90.2746C96.7474 90.1815 81.9789 75.4148 81.9789 57.2125C81.9789 39.046 96.6894 24.3016 114.893 24.1512C112.172 10.3825 100.03 0 85.4632 0H30Z" fill="#FFCA28"/>
  </svg>
);

const LandingPage = () => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const features = [
    { 
      icon: <Sparkles className="w-8 h-8 text-yellow-400" />, 
      title: "AI-Powered",
      description: "Uses AI to summarize lengthy web articles" 
    },
    { 
      icon: <Share2 className="w-8 h-8 text-yellow-400" />, 
      title: "Quick Share",
      description: "One-tap sharing" 
    },
    {
      icon: <Headphones className="w-8 h-8 text-yellow-400" />,
      title: "Voice Playback",
      description: "Listen on the go"
    },
    {
      icon: <Settings className="w-8 h-8 text-yellow-400" />,
      title: "Beautiful Themes",
      description: "Select from a list of beautiful templates"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white font-sans">
      {/* Link Banner */}
      <LinkBanner />
      
      {/* Nav */}
      <nav className="container mx-auto px-4 py-6 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <BiteSizRLogo />
          <span className="text-2xl font-bold">BiteSizR</span>
        </div>
        <button 
          onClick={() => {
            setIsDownloadModalOpen(true);
            // Analytics tracking for Try for Free button
            if (typeof window !== 'undefined' && (window as any).gtag) {
              (window as any).gtag('event', 'try_for_free_clicked', {
                event_category: 'engagement',
                event_label: 'header_cta'
              });
            }
          }}
          className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold px-6 py-2 rounded-full transition-colors"
        >
          Try for Free
        </button>
      </nav>

      {/* Hero Section */}
      <header className="container mx-auto px-4 py-16">
        {/* Desktop: flex-row, Mobile: flex-col */}
        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12 lg:gap-16">
          
          {/* Left side - Content (Desktop) / Top (Mobile) */}
          <div className="flex-1 text-center lg:text-left">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Summarize, Save & Share<br />
              Beautiful Content
            </h1>
            <p className="text-xl text-gray-400 mb-8 max-w-2xl lg:max-w-none mx-auto lg:mx-0">
              Powered by AI to give you quick, accurate summaries of <span className="font-bold text-white">web articles</span>. 
              Share bitesize stories faster than ever.
            </p>



            {/* Download Buttons */}
            <DownloadButtons />
          </div>

          {/* Right side - Mobile Screens (Desktop) / Bottom (Mobile) */}
          <div className="flex-none w-full max-w-md lg:max-w-sm xl:max-w-lg">
            <div className="relative">
              <img 
                src="/mobilescreens.png" 
                alt="BiteSizR Mobile App Screenshots"
                className="w-full h-auto rounded-2xl shadow-2xl"
                width="1160"
                height="1196"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Features */}
      <section className="container mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold mb-8 px-4">Integrated with</h2>
        <div className="grid grid-cols-2 gap-4">
          {features.map((feature, index) => (
            <div key={index} className="bg-gray-800 rounded-xl p-6">
              <div className="mb-4">
                {feature.icon}
              </div>
              <h3 className="text-lg font-semibold mb-1">
                {feature.title}
              </h3>
              <p className="text-gray-400 text-sm">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>
      <Footer/>

      {/* Download Modal */}
      <Modal
        isOpen={isDownloadModalOpen}
        onClose={() => {
          setIsDownloadModalOpen(false);
          // Analytics tracking for modal close
          if (typeof window !== 'undefined' && (window as any).gtag) {
            (window as any).gtag('event', 'download_modal_closed', {
              event_category: 'engagement',
              event_label: 'modal_interaction'
            });
          }
        }}
        title="Download BiteSizR"
      >
        <div className="text-center">
          <p className="text-gray-300 mb-6">
            Get started with BiteSizR on your mobile device
          </p>
          <DownloadButtons variant="centered" />
        </div>
      </Modal>
    </div>
  );
};

export default LandingPage;